import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, Shield, Bot, Braces, Sparkles } from 'lucide-react';
import { Container, PageHeader } from '@vnetwork/ui/layout';
import { Button, Skeleton, Badge } from '@vnetwork/ui/primitives';
import { Alert, useToast } from '@vnetwork/ui/feedback';
import { useAuth } from '@/auth/AuthProvider';
import { useProviders, useActivateService } from '@/api/service-provisioning';
import { trackServiceActivate } from '@/lib/analytics-events';
import { WAAP_PROVIDER, WAAP_PRODUCT, waapConsoleRoute } from './waap-service';
import { OnboardingCss } from './onboarding-css';
import {
  heroWrap,
  heroIcon,
  heroEyebrow,
  heroTitle,
  heroLede,
  stepList,
  stepRow,
  markerAccent,
  stepTitle,
  stepDesc,
} from './onboarding-styles';

/**
 * WAAP catalog activation landing — the in-catalog first-run screen when the
 * org has no WAAP service yet. Same activation-hero visual language as the
 * Healthcheck / OSS / SSL onboarding: activating provisions the standalone
 * `cobra` WAAP service (region + protection edge auto-bound), then drops the
 * customer into its console. Rendered by ServiceCatalogDetail in place of the
 * generic locked-console preview for the WAAP product.
 */
export function WaapActivateLanding({ onBack }: { onBack?: () => void }) {
  const navigate = useNavigate();
  const { show } = useToast();
  const { user } = useAuth();
  const { data: providers = [], isLoading: providersLoading } = useProviders();

  // WAAP is provisioned on its own `cobra` provider; only an active provider can
  // be provisioned self-serve.
  const cobra = useMemo(
    () =>
      providers.find(
        (p) => p.name?.trim().toLowerCase() === WAAP_PROVIDER && p.status === 'active',
      ),
    [providers],
  );

  const activate = useActivateService();
  const email = user?.email ?? '';
  const unavailable = !providersLoading && !cobra;
  const canActivate = !!cobra && !!email && !activate.isPending;

  function handleActivate() {
    if (!cobra || !canActivate) return;
    activate.mutate(
      { serviceName: WAAP_PRODUCT.serviceName, providerId: cobra.id, email },
      {
        onSuccess: (service) => {
          trackServiceActivate(WAAP_PROVIDER, 'waap');
          show({ tone: 'success', title: 'WAAP activated' });
          // Straight into the guided setup — the wizard auto-skips its
          // Resume/Restart step on a fresh service and opens at Domain & Origin,
          // so activation flows directly into adding the first website.
          navigate(`${waapConsoleRoute(service.id)}/onboarding`);
        },
        onError: (err) =>
          show({
            tone: 'danger',
            title: 'Activation failed',
            description: err.message || 'Could not activate WAAP. Please try again.',
          }),
      },
    );
  }

  return (
    <Container size="xl">
      <PageHeader
        icon={<ShieldCheck size={22} />}
        title={
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--vn-space-2)' }}>
            Web Application &amp; API Protection
            <Badge tone="info" appearance="subtle" size="sm">
              Beta
            </Badge>
          </span>
        }
        description="AI-WAF, bot management, rate limiting and API security for your websites."
        actions={
          onBack ? (
            <Button variant="ghost" startIcon={<ArrowLeft size={15} />} onClick={onBack}>
              Back to services
            </Button>
          ) : undefined
        }
      />

      {providersLoading ? (
        <Skeleton style={{ height: 200 }} />
      ) : unavailable ? (
        <Alert tone="warning" title={`${WAAP_PRODUCT.displayName} is not available yet`}>
          {WAAP_PRODUCT.displayName} is not available for self-serve activation on your account yet.
          Please contact our team to enable it.
        </Alert>
      ) : (
        <div className="vn-ob-hero" style={heroWrap}>
          <OnboardingCss />

          <span className="vn-ob-halo" style={heroIcon} aria-hidden>
            <ShieldCheck size={30} />
          </span>

          <p style={heroEyebrow}>Web Application &amp; API Protection</p>
          <h2 style={heroTitle}>Activate WAAP for your organization</h2>
          <p style={heroLede}>
            Provision a protection edge, then a guided setup walks you through adding your website,
            issuing SSL and turning on AI-WAF — it only takes a moment.
          </p>

          <Button
            size="lg"
            loading={activate.isPending}
            startIcon={<Sparkles size={16} />}
            onClick={handleActivate}
            disabled={!canActivate}
            style={{ marginTop: 'var(--vn-space-2)' }}
          >
            {activate.isPending ? 'Activating…' : 'Activate Web Application & API Protection'}
          </Button>

          <div style={{ ...stepList, marginTop: 'var(--vn-space-5)' }}>
            {PERKS.map((p) => (
              <div key={p.title} style={stepRow}>
                <span style={markerAccent}>{p.icon}</span>
                <div style={{ display: 'grid', gap: 2 }}>
                  <p style={stepTitle}>{p.title}</p>
                  <p style={stepDesc}>{p.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Container>
  );
}

const PERKS = [
  { icon: <Shield size={16} />, title: 'AI-WAF', desc: 'AI-driven web application firewall blocks OWASP and zero-day attacks.' },
  { icon: <Bot size={16} />, title: 'Bot management', desc: 'Detect and mitigate malicious bots, scrapers and automated abuse.' },
  { icon: <Braces size={16} />, title: 'API protection', desc: 'Discover your APIs and guard them against abuse and data loss.' },
];

export default WaapActivateLanding;
